/**
 * identity3.mjs — three questions that get smushed together, and the token that remembers.
 *
 * FOUNDER, 2026-08-14: "ch5 next".
 *
 * ── WHAT THE CHAPTER TURNS ON ─────────────────────────────────────────────────────────────────
 * §2, and it says so itself: "Mixing them up sounds like a small thing. It is not — it is what
 * separates a literate user from a confused one." The three are IDENTIFICATION (who you claim to
 * be), AUTHENTICATION (can you prove it), AUTHORIZATION (what you may then do) — and the section's
 * payload is that a system can get each wrong INDEPENDENTLY, with different fixes.
 *
 * So this engine keeps the three stages genuinely separate: each has its own switch, each fails on
 * its own, and the diagnosis names which one. Modelling sign-in as one boolean would collapse the
 * distinction the whole chapter exists to draw.
 *
 * ── THE AUTHORIZATION FAILURE IS THE ONE WORTH BUILDING ───────────────────────────────────────
 * Everyone has met a wrong password. Almost nobody has watched a CORRECTLY signed-in person be
 * handed a stranger's data — which is his own example: "your account, but accidentally also your
 * neighbour's data, because the system did not check carefully enough whose data this row belonged
 * to." Nothing is hacked, nobody's password is weak, and the system is broken. That is the demo.
 */

export const SOURCE_2 = 'Code101 module 05 §2 "Three questions: who, prove it, and what are you allowed to do?"';
export const SOURCE_5 = 'Code101 module 05 §5 "How a system remembers you after sign-in — sessions and tokens"';

/** The three, in his words and his order. */
export const QUESTIONS = Object.freeze([
  { id: 'identification', name: 'Identification', asks: 'who are you claiming to be?', eg: '(You type your email or username.)' },
  { id: 'authentication', name: 'Authentication', asks: 'can you prove that claim?', eg: '(You type your password, or your OTP, or your fingerprint.)' },
  { id: 'authorization', name: 'Authorization', asks: 'given that you are who you claim to be, what are you allowed to do?', eg: '(Read your own email but not someone else\'s.)' },
]);

/** §2's diagnosis, quoted — the payoff a learner can use the same afternoon. */
export const IN_PLAIN_SPEECH = Object.freeze({
  authentication: 'When you hear someone say "I got hacked," it almost always means authentication failed (someone got the password).',
  authorization: 'When you hear "the system leaked customer data," it almost always means authorization failed (the system showed records to people who should not have been able to see them).',
});
export const THE_POINT = 'These are different failures, with different fixes.';
export const WHY_IT_MATTERS = 'Mixing them up sounds like a small thing. It is not — it is what separates a literate user from a confused one.';

/**
 * Maya is the module's own character — §5: "ah, this is Maya, who signed in at 10:14 am from this
 * IP". Her neighbour is unnamed and his data is deliberately dull: the point of the leak is that it
 * happened, not what it said. Inventing a juicy secret would teach that leaks matter when the data
 * is interesting, which is the opposite of true.
 */
export const ACCOUNTS = Object.freeze([
  { id: 'maya', who: 'maya@example.com', label: 'Maya', list: ['rice 2kg', 'dal 1kg', 'tea'] },
  { id: 'neighbour', who: 'neighbour@example.com', label: 'the neighbour', list: ['milk', 'bread'] },
]);
/* NOT A CREDENTIAL, and deliberately not named like one.
   The first version called this RIGHT_PASSWORD and the repo's secret-floor guard blocked the
   commit — correctly. A constant named RIGHT_PASSWORD holding a string is indistinguishable, to a
   scanner and to a human skimming a diff, from a real password someone pasted in. The guard offers
   a bypass for false positives and taking it would have been the wrong move twice: it would train
   the next person to wave the guard through, and it would leave a genuinely misleading name in the
   tree. Renamed to what it is — a phrase PRINTED ON THE SCREEN beside the box, so the learner is
   never guessing. It unlocks nothing and guards nothing. */
export const SHOWN_PHRASE = 'open-sesame';

/**
 * One sign-in attempt, walked as three separate stages.
 *
 * `checksOwnership` is the authorization switch, and it is the one thing in this lab that is a
 * BUILD QUALITY rather than a user action — §2's "the system did not check carefully enough".
 */
export function attempt({ claimedAs = 'maya', password = '', checksOwnership = true, opening = 'maya' } = {}) {
  const account = ACCOUNTS.find((a) => a.id === claimedAs || a.who === claimedAs);
  const stages = [];

  /* 1 — IDENTIFICATION. Claiming to be someone is not a test you pass; it is a claim you make. The
     only way it fails is naming an account that does not exist, and even that is a courtesy: real
     systems often refuse to say so, to avoid confirming which addresses are registered. */
  const identified = Boolean(account);
  stages.push({ q: 'identification', ok: identified, says: identified
    ? `claim accepted: ${account.who}` : 'no such account' });
  if (!identified) return { stages, signedIn: false, failedAt: 'identification', token: null, saw: null };

  /* 2 — AUTHENTICATION. The proof. */
  const proved = password === SHOWN_PHRASE;
  stages.push({ q: 'authentication', ok: proved, says: proved
    ? 'proof accepted' : 'the proof does not match the claim' });
  if (!proved) return { stages, signedIn: false, failedAt: 'authentication', token: null, saw: null };

  /* 3 — AUTHORIZATION. Signed in, and now: what may this person actually reach? A system that does
     not check ownership hands over whatever was asked for. */
  const target = ACCOUNTS.find((a) => a.id === opening);
  const ownsIt = target && target.id === account.id;
  const allowed = checksOwnership ? ownsIt : true;
  stages.push({ q: 'authorization', ok: allowed, says: allowed
    ? (ownsIt ? 'this row belongs to you' : 'this row belongs to someone else — and nobody checked')
    : 'refused: this row belongs to someone else' });

  return {
    stages,
    signedIn: true,
    failedAt: allowed ? null : 'authorization',
    /* §5: "after you sign in successfully, the server gives your browser a small piece of paper".
       Issued on AUTHENTICATION, before the authorization question is even asked — which is why a
       leak can happen to a perfectly signed-in person. */
    token: { forWhom: account.id, issued: true },
    saw: allowed && target ? { owner: target.id, list: target.list } : null,
    /* THE FINDING, and the whole reason the lab exists: correctly signed in, handed someone else's
       data, and nothing was hacked. */
    leak: allowed && target && !ownsIt ? { owner: target.id, becauseOf: 'authorization' } : null,
  };
}

/** §5's four consequences of the token, quoted. Each is something a learner has seen. */
export const TOKEN_FACTS = Object.freeze([
  { id: 'signout', label: 'Sign out', quote: '"Signing out" is just deleting the token from your browser.' },
  { id: 'stay', label: 'Stay signed in on this device', quote: '"Stay signed in on this device" is the server issuing a longer-lived token.' },
  { id: 'everywhere', label: 'Sign out everywhere', quote: '"Sign out everywhere" is the server invalidating all tokens for your account, forcing every device to sign in again.' },
  { id: 'sudden', label: 'Why am I suddenly logged out?', quote: '"Why am I suddenly logged out of everything?" is usually because the server invalidated all tokens (often after a password reset or a security alert).' },
]);
export const STOLEN_DEVICE = 'Tokens are also why a stolen laptop is dangerous beyond the password — if the laptop is signed in, the attacker has the tokens, not just the password. Stolen device = stolen sessions.';
export const NO_MEMORY = 'The server, by default, has no memory between requests.';
